import { BPS_ONE } from '../damage/exact';

/**
 * **A composição canónica da vida máxima de um casco, e a única que existe.**
 *
 * ## O problema que este ficheiro resolve
 *
 * Há dois sítios que formam um casco - a caça e o duelo - e antes deste gate
 * eles não partilhavam nada: a caça multiplicava uma base por uma percentagem
 * somada, e o duelo lia uma constante. No dia em que uma joia passa a somar uma
 * percentagem de vida, essa diferença deixa de ser inofensiva: a mesma peça
 * teria de ser somada em dois sítios que ninguém obriga a concordar, e o
 * primeiro a divergir seria aquele em que ninguém tocou.
 *
 * Então há UMA lei, aqui, e as duas simulações atravessam para ela.
 *
 * ## A lei, e ela é uma linha
 *
 *     vidaMaxima = planaAntesDosPercentuais x (10 000 + somaDosBps) / 10 000
 *
 * `planaAntesDosPercentuais` é toda a vida-base acumulada: a da nave, a dos
 * pontos alocados, a plana dos artefactos, a plana do equipamento, e a de
 * qualquer fonte plana futura. `somaDosBps` é a SOMA de todas as percentagens de
 * vida máxima - os artefactos, o equipamento e o Coração do Titã - somadas
 * primeiro e aplicadas **uma única vez**.
 *
 * ## Somadas, e nunca multiplicadas em sequência
 *
 * É a mesma lei aditiva que `damage/exact.ts` já impõe do lado do tiro, e a
 * razão é a mesma: multiplicar em cadeia faz cada fonte nova valer mais por
 * existirem outras, que é a realimentação que o contrato V28D.2 proíbe.
 *
 * A prova aritmética do gate V28G.R é exatamente esta diferença. Com 1 000 de
 * vida-base, +500 de uma fonte plana, +500 de outra, +60 % do Coração do Titã e
 * +20 % de outra percentagem:
 *
 *     (1 000 + 500 + 500) x (1 + 0,60 + 0,20) = 3 600   <- correto
 *     2 000 x 1,60 x 1,20                     = 3 840   <- proibido
 *
 * ## Uma fronteira, e uma unidade (gate V28G.S.1)
 *
 * Havia aqui duas travessias, porque o duelo media vida em pontos INTEIROS e a
 * caça num `number` fracionário. A auditoria do V28G.S mostrou que essa divisão
 * era uma perda: nenhuma fonte legal produz pontos inteiros - o `flatMaxHealth`
 * de um componente é sorteado por milli e o `maxHealthPercent` por bps - e o
 * duelo truncava até 0,999 de vida que a caça tinha.
 *
 * Agora há **uma** saída, {@link composeMaxHealthMilli}, em milli e em `bigint`.
 * O duelo transporta-a tal e qual; a caça divide-a por mil para a sua física em
 * vírgula flutuante, que é anterior a tudo isto e que este gate não migra.
 * Nenhum dos dois arredonda, e por isso os dois têm a MESMA vida.
 */

/**
 * **A lei, em `bigint`.** Puro, exato, e sem estado.
 *
 * ## Porque `bigint`
 *
 * Porque nem a vida plana nem a soma das percentagens têm tecto de jogo. Um
 * `number` aqui seria um tecto de item escrito num tipo, que é o que o
 * `CLAUDE.md` proíbe - e o que o Coração do Titã produziria no dia em que uma
 * fonte plana crescesse muito.
 *
 * ## A identidade, e porque ela é estrutural
 *
 * Sem percentagem nenhuma a função devolve o argumento **pelo mesmo `bigint`**,
 * sem multiplicar e sem dividir. Não é uma otimização: é a garantia de que um
 * casco sem esta passiva produz exatamente o teto que produzia antes deste gate
 * existir, e consegui-lo por construção é diferente de o conseguir por a
 * aritmética calhar devolver o mesmo número.
 *
 * ## Uma truncatura, e uma só
 *
 * No fim, depois de as percentagens serem somadas. Truncar por fonte faria duas
 * percentagens pequenas desaparecerem uma de cada vez.
 */
export function composeMaxHealthExact(flatBeforePercent: bigint, summedBps: bigint): bigint {
  if (flatBeforePercent < 0n) {
    throw new RangeError(`flat maximum health is never negative, got ${flatBeforePercent}`);
  }
  if (BPS_ONE + summedBps < 0n) {
    throw new RangeError(`a maximum-health bonus never removes the whole hull, got ${summedBps}`);
  }
  if (summedBps === 0n) {
    /* Sem percentagem, o teto é o MESMO valor. Ver a nota acima. */
    return flatBeforePercent;
  }
  return (flatBeforePercent * (BPS_ONE + summedBps)) / BPS_ONE;
}

/** 1 000 milli-vida = 1 ponto de vida. A unidade EXATA, nomeada uma vez. */
export const HEALTH_MILLI_PER_UNIT = 1000;

/**
 * **A mesma lei, parada em MILLI - e é onde ela para** (gate V28G.S.1).
 *
 * ## Porque milli é a unidade, e não os pontos
 *
 * Porque nenhuma fonte legal produz vida em pontos inteiros. A auditoria do
 * V28G.S mediu-o no catálogo: o `flatMaxHealth` de um componente é sorteado por
 * MILLI sobre `[2 000, 12 000]`, e o `maxHealthPercent` por BPS sobre
 * `[150, 900]`. Uma Câmara comum vale 102,001 ou 102,999 de vida; um Mother
 * Ship nível 1 vale 108,12. O milésimo é a menor coisa que existe, portanto é
 * nele que a vida é exata.
 *
 * ## Uma quantização, e uma só
 *
 * `Math.round` para milli, e depois a multiplicação exata em `bigint`. Havia
 * antes uma segunda função - `composeMaxHealth` - que dividia isto de volta
 * para um `number` e devolvia o argumento intacto quando não havia percentagem.
 * Ela foi REMOVIDA: era uma segunda expressão da mesma lei, e quem quer a
 * unidade fracionária da caça divide este `bigint` por mil num sítio só
 * (`hunt/account-max-health.ts`).
 *
 * O `Math.round` de entrada não é um arredondamento de vida: é a leitura de uma
 * soma que já vive em milésimos (`EquipmentTotals.maxHealthMilli`) e que só
 * atravessou um `double` para ser somada. O que sai daqui é exato.
 */
export function composeMaxHealthMilli(flatBeforePercent: number, summedBps: bigint): bigint {
  if (!Number.isFinite(flatBeforePercent)) {
    throw new RangeError(`flat maximum health must be finite, got ${flatBeforePercent}`);
  }
  if (flatBeforePercent < 0) {
    throw new RangeError(`flat maximum health is never negative, got ${flatBeforePercent}`);
  }
  const scaled = Math.round(flatBeforePercent * HEALTH_MILLI_PER_UNIT);
  /*
   * Finito e inteiro, e deliberadamente NÃO `assertSafeInteger`.
   *
   * A vida da caça não tem tecto: os pontos alocados chegam a 2^40 e cada um
   * vale dez, portanto uma conta absurda mas LEGAL passa dos 9 x 10^15 milli que
   * um inteiro seguro guarda. Recusá-la aqui seria pôr um tecto de progressão
   * numa conversão de unidade, que é exatamente o que o `CLAUDE.md` proíbe.
   *
   * O que se perde acima dessa fronteira já estava perdido: a vida da caça é um
   * `double` desde que existe, e `Math.round` de um float dessa magnitude devolve
   * o próprio float, que `BigInt` converte exatamente. A regra sobre não aceitar
   * um valor malformado continua a ser cumprida - o que é recusado é o que não é
   * finito, e não o que é grande.
   */
  if (!Number.isFinite(scaled)) {
    throw new RangeError(`flat maximum health scaled to milli is not finite: ${scaled}`);
  }
  return composeMaxHealthExact(BigInt(scaled), summedBps);
}
